import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Icon, Input, type IconNames } from '@/shared/ui';

import {
  CATALOG_SOURCES,
  filterCatalogItems,
  PartnerTemplateBadge,
  TemplateCatalogFilters,
  type CatalogFilter,
} from '@/entities/templates';

import type { GalleryItem } from '../../config';
import { searchGalleryItems } from '../../lib';

import styles from './TemplateGallery.module.scss';

const INITIAL_FILTER: CatalogFilter = {
  source: 'ours',
  department: null,
  group: null,
};

type Props = {
  items: GalleryItem[];
  disabled?: boolean;
  className?: string;
  onSelect: (item: GalleryItem) => void;
};

const getItemIcon = (item: GalleryItem): IconNames => {
  if (item.kind === 'consultation') {
    return 'comment2';
  }
  return item.blockNote ? 'tiles' : 'doc';
};

export const TemplateGallery: FC<Props> = ({
  items,
  disabled,
  className,
  onSelect,
}) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<CatalogFilter>(INITIAL_FILTER);
  const visible = searchGalleryItems(filterCatalogItems(items, filter), query);

  return (
    <div
      className={cn(styles.gallery, className)}
      data-component="transcriptor-template-gallery"
    >
      <Input
        name="transcriptor-template-search"
        icon="search"
        inputClassName={styles.search}
        disabled={disabled}
        aria-label={formatMessage({
          id: 'workspace.template.search',
          defaultMessage: 'Search templates…',
        })}
        placeholder={formatMessage({
          id: 'workspace.template.search',
          defaultMessage: 'Search templates…',
        })}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <TemplateCatalogFilters
        items={items}
        filter={filter}
        sources={CATALOG_SOURCES}
        disabled={disabled}
        onChange={setFilter}
      />
      <div className={styles.grid} role="list">
        {visible.map((item) => (
          <button
            type="button"
            role="listitem"
            key={item.id}
            className={styles.card}
            disabled={disabled}
            data-component="transcriptor-template-card"
            onClick={() => onSelect(item)}
          >
            <span className={styles.icon} aria-hidden>
              <Icon name={getItemIcon(item)} size={18} />
            </span>
            <span className={cn('p3b', styles.name)}>{item.name}</span>
            {item.partner !== undefined && (
              <PartnerTemplateBadge
                partner={item.partner}
                className={styles.badge}
              />
            )}
          </button>
        ))}
      </div>
      {visible.length === 0 && (
        <p className={cn('p3', styles.empty)}>
          {filter.source === 'saved' && !query.trim() && !filter.department ? (
            <FormattedMessage
              id="workspace.templates.mineEmpty"
              defaultMessage="Templates you save from the library or edit appear here."
            />
          ) : items.length === 0 ? (
            <FormattedMessage
              id="transcriptorStart.noTemplates"
              defaultMessage="No templates in this language. Choose another language in the recording settings or set templates up in the organization settings."
            />
          ) : (
            <FormattedMessage
              id="workspace.template.noResults"
              defaultMessage="No templates match this search."
            />
          )}
        </p>
      )}
    </div>
  );
};
